import type { VoidFunctionComponent } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import useSavedFilters from './hooks/useSavedFilters';
import DropdownButton from '../generic/DropdownButton';
import * as IssuesV2Store from '../../redux/issuesV2';
import * as SavedFiltersStore from '../../redux/savedFilters';
import useFetchIssuesAction from '../issues/IssuesInbox/hooks/useFetchIssuesAction';
import type { ReduxStore } from '../../redux/store';
import { NO_FILTER_OPTION_LABEL } from '../../helpers/savedFilters';

const UNFILTERED_OPTION: FilterOption = {
  id: null,
  name: NO_FILTER_OPTION_LABEL,
  isProtected: true,
};

//  Represents one of the dropdown items.
type FilterOption = {
  id: number | null;
  name: string;
  isProtected?: boolean;
};

type Props = {
  filters: FilterOption[];
  currentFilter: FilterOption;
  isLoading: boolean;
  onChange: (filter: FilterOption['id']) => void;
};

/**
 * SavedFiltersDropdownList renders the list of saved filters
 * for a given org + issueCategory combo.
 */
const SavedFiltersDropdownList: VoidFunctionComponent<Props> = ({ filters, currentFilter, isLoading, onChange }) => (
  <DropdownButton
    id="saved-filters-list-dropdown"
    title={isLoading ? 'Loading...' : <span className="dropdown-button-text">{currentFilter.name}</span>}
    size="large"
    onSelect={onChange}
    testId="saved-filters-list-dropdown"
    isFullWidth
  >
    {filters.map(({ id, name }) => (
      <DropdownButton.Item
        key={id}
        eventKey={id}
        className={cx({
          'fossa-dropdown-button__item--selected': currentFilter.id === id,
        })}
      >
        <span className="fossa-dropdown-button__item__name">{name}</span>
        {id && <span>(id: {id})</span>}
      </DropdownButton.Item>
    ))}
  </DropdownButton>
);

/**
 * SavedFiltersDropdownListContainer is responsible for a few things:
 *   - Fetching the list of saved filters for this org + issueCategory combo.
 *   - Handle dispatching the action which applies the current filter (and in turn re-fetches the issues).
 */
const SavedFiltersDropdownListContainer: VoidFunctionComponent = () => {
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const {
    filters,
    status: { loading: isLoading },
  } = useSavedFilters(category);
  const currentFilter = useSelector((state: ReduxStore) =>
    SavedFiltersStore.selectors.getCurrentFilterByCategory(state, category),
  );
  //  Wrapped in a useFetchIssuesAction so that we re-fetch issues when the currentFilter is reset
  const setCurrentFilter = useFetchIssuesAction(SavedFiltersStore.actions.setCurrentFilter);

  const filterOptions: FilterOption[] = [UNFILTERED_OPTION, ...filters.map(({ id, name }) => ({ id, name }))];

  const onChange = (nextFilterId: number) => {
    //  Handle "unsetting" the saved filter.
    if (nextFilterId === null) {
      setCurrentFilter(null);
      return;
    }
    //  If the filter is actually changing, dispatch it
    if (nextFilterId !== currentFilter?.id) {
      setCurrentFilter(nextFilterId);
    }
  };

  return (
    <SavedFiltersDropdownList
      filters={filterOptions}
      currentFilter={currentFilter || UNFILTERED_OPTION}
      isLoading={isLoading}
      onChange={onChange}
    />
  );
};

export default SavedFiltersDropdownListContainer;
